import React, { useEffect, useRef, useState } from "react"
import * as styles from "./ScrollableGallery.module.css"
import SwipeGallery from "../SwipeGallery/SwipeGallery"
import CaretLeftSVG from "../../../../images/decorative/caret-left.inline.svg"
import CaretRightSVG from "../../../../images/decorative/caret-right.inline.svg"
import classNames from "classnames"

type ScrollableGalleryProps = {
  children: React.ReactElement
}

const ScrollableGallery = ({ children }: ScrollableGalleryProps) => {
  //
  const [activeItem, setActiveItem] = useState(0)

  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault()
      if (!containerRef.current) return
      const countChildren = React.Children.count(children) - 1
      if (
        e.deltaY > 0 &&
        (activeItem === countChildren - 2 || activeItem === countChildren - 1)
      ) {
        containerRef.current.scrollLeft += countChildren * 350
        setActiveItem(preActiveItem => preActiveItem + 1)
      } else if (e.deltaY < 0 && activeItem === countChildren) {
        setActiveItem(preActiveItem => preActiveItem - 1)
      } else if (activeItem !== countChildren) {
        containerRef.current.scrollLeft += e.deltaY
        setActiveItem(Math.ceil(containerRef.current.scrollLeft / 350))
      }
    }

    const containerElement = containerRef.current
    if (containerElement) {
      containerElement.addEventListener("wheel", handleWheel, {
        passive: false,
      })
    }
    return () => {
      if (containerElement) {
        containerElement.removeEventListener("wheel", handleWheel)
      }
    }
  }, [containerRef, activeItem, setActiveItem])

  // Handle Carets
  const handleCarets = (direction: "left" | "right") => {
    if (!containerRef.current) return
    const scroll = 350 * activeItem
    if (direction === "left") {
      containerRef.current.scrollLeft = scroll - 350
      setActiveItem(preActiveItem => preActiveItem - 1)
    }
    if (direction === "right") {
      containerRef.current.scrollLeft = scroll + 350
      setActiveItem(preActiveItem => preActiveItem + 1)
    }
  }

  // Handle Circles
  const handleCircles = (itemIndex: number) => {
    if (!containerRef.current) return
    containerRef.current.scrollLeft = 350 * itemIndex
    setActiveItem(itemIndex)
  }

  return (
    <div>
      <div ref={containerRef} className={styles.container}>
        <div className={styles.boxContent}>
          <SwipeGallery>{children}</SwipeGallery>
        </div>
      </div>
      <div className={styles.caretContent}>
        <button
          className={classNames(styles.caret, {
            [styles.disable]: activeItem === 0,
          })}
          disabled={activeItem === 0}
          onClick={() => handleCarets("left")}
        >
          <CaretLeftSVG />
        </button>

        {React.Children.toArray(children).map((_: any, index: number) => (
          <button
            key={index}
            className={classNames(styles.circle, {
              [styles.active]: activeItem === index,
            })}
            onClick={() => handleCircles(index)}
          >
            <div />
          </button>
        ))}

        <button
          className={classNames(styles.caret, {
            [styles.disable]: activeItem >= React.Children.count(children) - 1,
          })}
          disabled={activeItem >= React.Children.count(children) - 1}
          onClick={() => handleCarets("right")}
        >
          <CaretRightSVG />
        </button>
      </div>
    </div>
  )
}

export default ScrollableGallery
